import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Utils
import useMountedState from "../../hooks/useMountedState.js";

// Components
import Modal from "../../components/Modal.js";

// Style
import {flex} from "../../style/components/mixins.js";
import {pad} from "../../style/components/variables.js";
import {
  HeadingCenter,
  Form,
  Text,
  Button,
  FormGroup,
  Span,
  Inline,
  Warning
} from "../../style/components/general.js";
import {getAncestryName} from "./helpers.js";
import {FIELD} from "../../utils/builder.js";

const ModalDeleteElement = ({
  visible,
  setVisible,
  targetElement,
  fields,
  builder,
  isState = false,
  notifications,
  persistDelete,
  hasBackButton = false,
  goBack = () => {}
}) => {
  const fieldsCoalesced = useMemo(() => {
    if (targetElement) return [targetElement];
    return fields.map(field => builder.byId[field]) || [];
  }, [fields, targetElement, builder]);

  const isMounted = useMountedState();

  const [deleteNotifications, setDeleteNotifications] = useState([]);

  const hasDependants = useCallback(
    field => {
      const {name} = field;
      const matches = Object.values(builder.byId).filter(
        ({formula, condition, deleted, name: fieldName}) => {
          if (fields && fields.includes(fieldName)) return false;
          if (deleted) return false;
          let itemMatches = [];
          if (formula)
            itemMatches = [
              ...itemMatches,
              ...formula.filter(item => item.type === "variable" && item.value === name)
            ];

          if (condition?.list)
            itemMatches = [...itemMatches, ...condition.list.filter(item => item.depends === name)];

          return itemMatches.length > 0;
        }
      );

      return matches;
    },
    [builder, fields]
  );

  const hasChildGenerates = useCallback(
    field => {
      const {element, name, children} = field;
      if (!children || element === FIELD) return false;

      const matches = Object.values(builder.byId).filter(
        ({formula, condition, parentName: elementParent, deleted, name: fieldName}) => {
          if (deleted) return false;
          if (fields && fields.includes(fieldName)) return false;
          let withinParent = false;

          if (name === elementParent) withinParent = true;
          else {
            const parent = builder.byId[elementParent];
            if (parent?.parentName === name) withinParent = true;
          }

          if (withinParent) return false;

          let itemMatches = [];
          if (formula)
            itemMatches = [
              ...itemMatches,
              ...formula.filter(
                item => item.type === "variable" && children.some(val => val === item.name)
              )
            ];

          if (condition?.list)
            itemMatches = [
              ...itemMatches,
              ...condition.list.filter(item => children.some(val => val === item.depends))
            ];

          return itemMatches.length > 0;
        }
      );

      return matches.filter(({toggle}) => toggle);
    },
    [builder, fields]
  );

  useEffect(() => {
    if (isMounted() && notifications?.allIds?.length > 0) {
      const {allIds, byId} = notifications;
      const matches = allIds.filter(id =>
        fieldsCoalesced.some(
          ({name}) =>
            ((isState && byId[id].isState) || (!isState && !byId[id].isState)) &&
            (name === byId[id].depends ||
              byId[id].conditionArray?.some?.(item => item.depends === name))
        )
      );
      setDeleteNotifications(matches);
    }
  }, [isMounted, notifications, fieldsCoalesced, isState]);

  const handleSubmit = e => {
    e.preventDefault();
    persistDelete(targetElement, deleteNotifications);
  };

  let canDelete = true;

  const renderMessage = field => {
    const {element} = field;

    const dependants = hasDependants(field);

    if (dependants?.length) {
      canDelete = false;
      return (
        <>
          <Text>
            Unable to delete! {targetElement ? "This field" : getAncestryName(field, builder)} is
            being used in a formula or conditional:
          </Text>
          <DepList>
            {dependants.map(({name: depName}) => (
              <div key={depName}>
                &nbsp;&nbsp;&nbsp;{getAncestryName(builder.byId[depName], builder)}
              </div>
            ))}
          </DepList>
        </>
      );
    }

    const childGenerates = hasChildGenerates(field);

    if (childGenerates?.length) {
      canDelete = false;
      return (
        <>
          <Text>
            Unable to delete! {targetElement ? "This element" : getAncestryName(field, builder)}{" "}
            contains fields that are being used in a formula or conditional:
          </Text>
          <DepList>
            {childGenerates.map(({name: depName}) => (
              <div key={depName}>
                &nbsp;&nbsp;&nbsp;{getAncestryName(builder.byId[depName], builder)}
              </div>
            ))}
          </DepList>
        </>
      );
    }

    if (deleteNotifications?.length > 0)
      return (
        <Text>
          {targetElement ? `${targetElement.label} is` : "The selected elements are"} used in one or
          more field notifications. Are you sure you want to delete{" "}
          {targetElement ? `this ${element}? Its` : "these elements? Their"} notifications{" "}
          <Span error>WILL BE DELETED</Span>. This action cannot be undone.
        </Text>
      );

    return null;
  };

  const renderMessages = () => {
    const messages = [];
    for (let i = 0; i < fieldsCoalesced.length; i++) {
      const field = fieldsCoalesced[i];
      const msg = renderMessage(field);
      if (msg)
        messages.push(
          <Fragment key={field.name}>
            {msg}
            <br />
          </Fragment>
        );
    }

    return messages;
  };

  const allMessages = renderMessages();

  return (
    <Modal visible={visible} setVisible={setVisible} hasBackButton={hasBackButton} goBack={goBack}>
      <ModalTitle>Delete Element</ModalTitle>

      <Form onSubmit={handleSubmit} noValidate>
        <FormGroup>
          {allMessages.length ? (
            allMessages
          ) : (
            <Text>
              Are you sure you want to delete{" "}
              {targetElement ? `this ${targetElement.element}` : "these elements"}? This action
              cannot be undone.
            </Text>
          )}
        </FormGroup>
        {targetElement && targetElement.tag?.toLowerCase() === "regulatory" && (
          <FormGroup>
            <Inline>
              <Warning strong small>
                Warning!
              </Warning>
              <Text>This field is a regulatory field.</Text>
            </Inline>
            <br />
          </FormGroup>
        )}
        {fields && fields.some(field => field?.tag?.toLowerCase() === "regulatory") && (
          <FormGroup>
            <Inline>
              <Warning strong small>
                Warning!
              </Warning>
              <Text>One or more selected fields are regulatory fields.</Text>
            </Inline>
            <br />
          </FormGroup>
        )}

        <Options>
          <Option
            type="button"
            onClick={() => (hasBackButton && goBack ? goBack() : setVisible(false))}>
            {hasBackButton && goBack ? "Go Back" : "Cancel"}
          </Option>
          {canDelete && <Delete type="submit">Delete</Delete>}
        </Options>
      </Form>
    </Modal>
  );
};

ModalDeleteElement.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  persistDelete: PropTypes.func.isRequired,
  targetElement: PropTypes.objectOf(PropTypes.any),
  fields: PropTypes.arrayOf(PropTypes.any),
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  notifications: PropTypes.objectOf(PropTypes.any),
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func,
  isState: PropTypes.bool
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin-bottom: ${pad * 2}px;
`;

const Options = styled.div`
  ${flex("row", "nowrap", "start", "center")};
  width: 100%;
`;

const Option = styled(Button)`
  margin: 0 ${pad / 2}px;
`;

const Delete = styled(Option)`
  background: ${({theme}) => theme.error};
  color: ${({theme}) => theme.tertiary};

  &:disabled {
    opacity: 0.6;
  }
`;

const DepList = styled.div`
  text-align: left;
  color: ${({theme}) => theme.secondary};
`;

export default ModalDeleteElement;
